import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { FileText, Upload, User as UserIcon, Bell, FileSignature, Calendar, Check, Clock, AlertTriangle, DollarSign, Receipt } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState({
    documents: 0,
    notifications: 0,
    contracts: 0
  });
  const [installments, setInstallments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingBoleto, setDownloadingBoleto] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [docsRes, notifRes, contractsRes, installmentsRes] = await Promise.all([
        axios.get(`${API}/documents`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/notifications/me/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { count: 0 } })),
        axios.get(`${API}/contracts/me`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/contracts/me/installments`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] }))
      ]);

      setStats({
        documents: docsRes.data?.length || 0,
        notifications: notifRes.data?.count || 0,
        contracts: contractsRes.data?.length || 0
      });
      
      setInstallments(installmentsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format date without timezone issues
  const formatDateBR = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  const getPaymentStatusConfig = (status) => {
    const configs = {
      pending: { label: 'Pendente', color: 'text-yellow-600', bgColor: 'bg-yellow-100', icon: Clock },
      paid: { label: 'Pago', color: 'text-green-600', bgColor: 'bg-green-100', icon: Check },
      overdue: { label: 'Em Atraso', color: 'text-red-600', bgColor: 'bg-red-100', icon: AlertTriangle },
      cancelled: { label: 'Cancelada', color: 'text-gray-400', bgColor: 'bg-gray-100', icon: AlertTriangle }
    };
    return configs[status] || configs.pending;
  };

  // Download boleto
  const handleBoletoDownload = async (contractId, installmentIndex, boletoName) => {
    setDownloadingBoleto(`${contractId}-${installmentIndex}`);
    try {
      // Use current origin to ensure correct URL in both production and staging
      const baseUrl = window.location.origin;
      const response = await axios.get(
        `${baseUrl}/api/contracts/${contractId}/installments/${installmentIndex}/boleto`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', boletoName || `boleto_parcela_${installmentIndex + 1}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading boleto:', error);
      alert('Erro ao baixar boleto');
    } finally {
      setDownloadingBoleto(null);
    }
  };

  // Calculate installment stats (exclude cancelled)
  const activeInstallments = installments.filter(i => i.status !== 'cancelled');
  const installmentStats = {
    total: activeInstallments.length,
    paid: activeInstallments.filter(i => i.status === 'paid').length,
    pending: activeInstallments.filter(i => i.status === 'pending').length,
    overdue: activeInstallments.filter(i => i.status === 'overdue').length,
    cancelled: installments.filter(i => i.status === 'cancelled').length
  };

  // Get upcoming installments (next 5 non-paid, non-cancelled)
  const upcomingInstallments = installments
    .filter(i => i.status !== 'paid' && i.status !== 'cancelled')
    .slice(0, 5);

  const statCards = [
    { 
      icon: FileSignature, 
      label: 'Contratos', 
      value: loading ? '...' : stats.contracts.toString(), 
      color: 'bg-purple-500', 
      link: '/dashboard/contracts' 
    },
    { 
      icon: FileText, 
      label: 'Documentos', 
      value: loading ? '...' : stats.documents.toString(), 
      color: 'bg-blue-500', 
      link: '/dashboard/documents' 
    },
    { 
      icon: Bell, 
      label: 'Notificações', 
      value: loading ? '...' : stats.notifications > 0 ? `${stats.notifications} nova(s)` : '0', 
      color: stats.notifications > 0 ? 'bg-red-500' : 'bg-gray-500', 
      link: '/dashboard/notifications' 
    },
    { 
      icon: UserIcon, 
      label: 'Perfil', 
      value: 'Completo', 
      color: 'bg-green-500', 
      link: '/dashboard/profile' 
    }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">Bem-vindo(a) de volta, {user?.full_name}!</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link
              key={stat.label}
              to={stat.link}
              className="bg-white rounded-lg shadow-md p-4 border-2 border-gray-200 hover:border-arcano-blue transition-all"
            >
              <div className="flex items-center gap-3">
                <div className={`${stat.color} p-2 rounded-lg`}>
                  <Icon size={24} className="text-white" />
                </div>
                <div>
                  <p className="text-gray-600 text-xs">{stat.label}</p>
                  <p className="text-lg font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Installments Summary */}
      {installments.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 border-2 border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <DollarSign className="text-arcano-blue" />
              Minhas Parcelas
            </h2>
            <Link to="/dashboard/contracts" className="text-arcano-blue hover:underline text-sm">
              Ver todos os contratos →
            </Link>
          </div>

          {/* Installment Stats */}
          <div className={`grid ${installmentStats.cancelled > 0 ? 'grid-cols-5' : 'grid-cols-4'} gap-4 mb-6`}>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-700">{installmentStats.total}</p>
              <p className="text-xs text-gray-500">Ativas</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-700">{installmentStats.paid}</p>
              <p className="text-xs text-green-600">Pagas</p>
            </div>
            <div className="text-center p-3 bg-yellow-50 rounded-lg">
              <p className="text-2xl font-bold text-yellow-700">{installmentStats.pending}</p>
              <p className="text-xs text-yellow-600">Pendentes</p>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <p className="text-2xl font-bold text-red-700">{installmentStats.overdue}</p>
              <p className="text-xs text-red-600">Em Atraso</p>
            </div>
            {installmentStats.cancelled > 0 && (
              <div className="text-center p-3 bg-gray-100 rounded-lg">
                <p className="text-2xl font-bold text-gray-400">{installmentStats.cancelled}</p>
                <p className="text-xs text-gray-400">Canceladas</p>
              </div>
            )}
          </div>

          {/* Upcoming Installments */}
          {upcomingInstallments.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Próximas Parcelas</h3>
              <div className="space-y-2">
                {upcomingInstallments.map((installment, idx) => {
                  const statusConfig = getPaymentStatusConfig(installment.status);
                  const StatusIcon = statusConfig.icon;
                  const boletoKey = `${installment.contract_id}-${installment.installment_index}`;
                  
                  return (
                    <div
                      key={idx}
                      className={`flex items-center justify-between p-3 rounded-lg ${statusConfig.bgColor}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                          installment.status === 'overdue' ? 'bg-red-500' : 'bg-yellow-500'
                        }`}>
                          {installment.installment_number}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            Parcela {installment.installment_number}/{installment.total_installments}
                          </p>
                          <p className="text-sm text-gray-600">
                            R$ {installment.value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-medium text-gray-900 flex items-center gap-1">
                            <Calendar size={14} />
                            {formatDateBR(installment.due_date)}
                          </p>
                          <div className={`flex items-center gap-1 text-sm ${statusConfig.color}`}>
                            <StatusIcon size={14} />
                            <span>{statusConfig.label}</span>
                          </div>
                        </div>
                        
                        {/* Boleto Download Button */}
                        {installment.has_boleto && (
                          <button
                            onClick={() => handleBoletoDownload(
                              installment.contract_id, 
                              installment.installment_index, 
                              installment.boleto_name
                            )}
                            disabled={downloadingBoleto === boletoKey}
                            className="flex items-center gap-1 px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 text-sm"
                            title="Baixar Boleto"
                            data-testid={`download-boleto-${installment.installment_number}`}
                          >
                            {downloadingBoleto === boletoKey ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                              <>
                                <Receipt size={16} />
                                <span className="hidden sm:inline">Boleto</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {upcomingInstallments.length === 0 && installmentStats.paid === installmentStats.total && (
            <div className="text-center py-4">
              <Check size={48} className="mx-auto text-green-500 mb-2" />
              <p className="text-green-600 font-semibold">Todas as parcelas estão pagas!</p>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-md p-6 border-2 border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Ações Rápidas</h2>
        <div className="grid md:grid-cols-4 gap-4">
          <Link
            to="/dashboard/contracts"
            className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-md hover:border-arcano-blue transition-colors"
          >
            <FileSignature size={24} className="text-arcano-blue" />
            <div>
              <p className="font-semibold text-gray-900">Meus Contratos</p>
              <p className="text-sm text-gray-600">Ver detalhes</p>
            </div>
          </Link>
          <Link
            to="/dashboard/documents"
            className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-md hover:border-arcano-blue transition-colors"
          >
            <Upload size={24} className="text-arcano-blue" />
            <div>
              <p className="font-semibold text-gray-900">Enviar Documento</p>
              <p className="text-sm text-gray-600">Upload de arquivos</p>
            </div>
          </Link>
          <Link
            to="/dashboard/notifications"
            className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-md hover:border-arcano-blue transition-colors"
          >
            <Bell size={24} className="text-arcano-blue" />
            <div>
              <p className="font-semibold text-gray-900">Notificações</p>
              <p className="text-sm text-gray-600">Ver avisos</p>
            </div>
          </Link>
          <Link
            to="/dashboard/profile"
            className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-md hover:border-arcano-blue transition-colors"
          >
            <UserIcon size={24} className="text-arcano-blue" />
            <div>
              <p className="font-semibold text-gray-900">Editar Perfil</p>
              <p className="text-sm text-gray-600">Atualizar dados</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Welcome Card */}
      <div className="bg-gradient-to-r from-arcano-blue to-blue-600 rounded-lg shadow-md p-8 text-white">
        <h2 className="text-2xl font-bold mb-2">Bem-vindo à Arcano Projetos</h2>
        <p className="mb-4">
          Esta é sua área exclusiva onde você pode gerenciar seus documentos, acompanhar seus contratos e parcelas, e ficar por dentro das novidades.
        </p>
        <p className="text-blue-100">
          Caso precise de ajuda, entre em contato conosco pelo WhatsApp ou email.
        </p>
      </div>
    </div>
  );
};

export default Dashboard;
